import { useAppDispatch } from 'core/hooks/use-app-dispatch';
import { useExchangeTokenMutation } from 'feature/auth/api';
import { useAuthNonce, useAuthState } from 'feature/auth/hooks';
import { login, setLoggedOut, setUserCurrentInfo } from 'feature/auth/store';
import { Loader } from 'hfs-common-ui';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { RoutePaths } from 'core/models';
import { configAuth } from 'core/config/environment-variables';
import { useTranslationWrapper } from 'core/hooks/use-translation-wrapper';
import { InternationalizationNameSpace } from 'core/models/internationalization-namespace';
import { isApiResponse } from 'core/api';
import UserSalesCompanyCodesInformation from 'feature/auth/components/UserSalesCompanyCodesInformation';
import AlertBanner from 'core/components/AlertBanner';
import { testIds, automationIds } from 'feature/auth/models/test-ids';
import * as S from './styled';
import { DealerErrorType } from 'feature/auth/models/employee-types';

const Callback = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslationWrapper(InternationalizationNameSpace.Auth);
  const [exchangeToken, { isLoading, isSuccess, data, isError, error }] = useExchangeTokenMutation();
  const state = searchParams.get('state');
  const code = searchParams.get('code');
  const loggedOut = searchParams.get('logout');
  const message = searchParams.get('message');
  const errorIsam = searchParams.get('error');
  const { nonceCryptographicHash } = useAuthNonce();
  const { stateCryptographicHash } = useAuthState();
  const isOidcCallbackValid = state && code && nonceCryptographicHash && state === stateCryptographicHash;
  const [displayErrorBanner, setDisplayErrorBanner] = useState(true);
  const isDealerError = message === DealerErrorType.UnauthorizedDealer || message === DealerErrorType.ForbiddenDealer;

  useEffect(() => {
    if (isOidcCallbackValid) {
      exchangeToken({
        code: code ?? '',
        nonce: nonceCryptographicHash ?? 'nonceCryptographicHash',
        redirectUri: configAuth.oidcRedirectUrl,
      });
    }
  }, [code, exchangeToken, isOidcCallbackValid, nonceCryptographicHash, searchParams, stateCryptographicHash]);

  useEffect(() => {
    if (loggedOut) {
      dispatch(setLoggedOut());
      dispatch(setUserCurrentInfo(undefined));
    }
  }, [dispatch, loggedOut]);

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(login(data));
      navigate(RoutePaths.Dashboard);
      setDisplayErrorBanner(false);
    }
  }, [data, dispatch, isSuccess, navigate]);

  useEffect(() => {
    if (isError && isApiResponse(error)) {
      if (error.status === 401 || error.status === 403) {
        setDisplayErrorBanner(true);
        window.location.href = configAuth.oidcUrl
          .concat('/pkmslogout?filename=logout.html&targeturl=')
          .concat(encodeURIComponent(configAuth.oidcRedirectUrl.concat(`?logout=true&message=${error.data.detail}`)));
      }
    }
  }, [error, isError]);

  return (
    <S.CallbackWrapper>
      {isLoading && (
        <S.LoaderWrapper data-testid={testIds.auth.loadingAuth}>
          <Loader size="medium" />
        </S.LoaderWrapper>
      )}
      <S.SalesCompanyCodesWrapper>
        {displayErrorBanner && message && (
          <AlertBanner variant="error"
            ids={{ wrapper: automationIds.Login.noDealerErrorWrapper, message: automationIds.Login.noDealerError }}
            messageText={t(message)}
            iconType="notification.error_outlined"
          >
            {!isDealerError && <UserSalesCompanyCodesInformation size="small" />}
          </AlertBanner>
        )}
      </S.SalesCompanyCodesWrapper>

      {errorIsam && (
        <S.SalesCompanyCodesWrapper>
          {displayErrorBanner && (
            <AlertBanner variant="error"
              ids={{ wrapper: automationIds.Login.genericErrorWrapper, message: automationIds.Login.genericError }}
              messageText={t('AUTH:api.error.generic')}
              iconType="notification.error_outlined"
            >
              <UserSalesCompanyCodesInformation size="small" />
            </AlertBanner>
          )}
        </S.SalesCompanyCodesWrapper>
      )}
      {
        // place holder to be changed to a alert dialog later on (right now will not be visible due to the redirect)
        isError && isApiResponse(error) && error.status === 401 && (
          <S.SalesCompanyCodesWrapper>
            {displayErrorBanner && (
              <AlertBanner variant="error"
                ids={{ wrapper: automationIds.Login.apiErrorWrapper, message: automationIds.Login.apiError }}
                messageText={t('AUTH:api.error.generic')}
                iconType="notification.error_outlined"
              />
            )}
          </S.SalesCompanyCodesWrapper>
        )
      }
      {isError && isApiResponse(error) && error.status !== 403 && (
        <Navigate to="/auth" state={{ error: t('AUTH:api.error.generic') }} replace />
      )}
    </S.CallbackWrapper>
  );
};
export default Callback;
